import { useMsal } from "@azure/msal-react"
import { ExpandMore } from "@mui/icons-material"
import {
  Button,
  FormControl,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import Stack from "@mui/material/Stack"
import { usePathname, useRouter } from "next/navigation"
import { FC, MouseEvent, useEffect, useMemo, useState } from "react"

// import { Button } from "src/components/common/button"
import { Icon } from "src/components/common/icon"
import { create } from "src/helpers/bem"

import { getPath } from "src/helpers/paths"
import { resetStates } from "src/helpers/resetStates"
import { useTranslation } from "src/hooks/translation"

import { useLocale } from "src/states/locale"

import { addNotification } from "src/states/notifications"

import { messages } from "src/translations/messages/de"

import styles from "./AccountNavigation.module.scss"

const bem = create(styles, "AccountNavigation")

export const AccountNavigation: FC = () => {
  const { messages: translations } = useTranslation()
  const locale = useLocale()
  const pathname = usePathname()
  const router = useRouter()
  const { instance } = useMsal()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("xl"))
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedNavigationButton, setSelectedNavigationButton] = useState(0)
  const open = Boolean(anchorEl)
  const handleClickListItem = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }

  const navigationTranslations = translations.pages.account.overview.navigation
  const buttons: {
    title: string
    url: string
    selected: boolean
    icon: "profile" | "bookings" | "orders" | "service"
  }[] = useMemo(
    () => [
      {
        title: navigationTranslations.profile,
        url: getPath(locale, "account"),
        selected: pathname === getPath(locale, "account"),
        icon: "profile",
      },
      {
        title: navigationTranslations.bookings,
        url: getPath(locale, "bookings"),
        selected: pathname === getPath(locale, "bookings"),
        icon: "bookings",
      },
      {
        title: navigationTranslations.service,
        url: getPath(locale, "orders"),
        selected: pathname === getPath(locale, "orders"),
        icon: "orders",
      },
      {
        title: navigationTranslations.support,
        url: getPath(locale, "service"),
        selected: pathname === getPath(locale, "service"),
        icon: "service",
      },
    ],
    [locale, navigationTranslations, pathname],
  )

  useEffect(() => {
    const selectedButtonIndex = buttons.findIndex((button) => button.selected)
    if (selectedButtonIndex >= 0) {
      setSelectedNavigationButton(selectedButtonIndex)
    }
  }, [buttons])

  const handleLogout = async () => {
    instance
      .logoutRedirect({
        postLogoutRedirectUri: "/",
      })
      .catch((e) => {
        addNotification({
          variant: "error",
        })
      })
    await resetStates()
  }

  const renderNavigtion = () => {
    return (
      <>
        {buttons.map((button) => {
          return (
            <Button
              key={button.title}
              variant="outlined"
              className={
                button.selected
                  ? bem("navigation__button__selected")
                  : bem("navigation__button")
              }
              startIcon={
                <Icon
                  name={button.icon}
                  className={
                    button.selected
                      ? bem("navigation__icon__selected")
                      : bem("navigation__icon")
                  }
                />
              }
              onClick={() => {
                router.push(button.url)
              }}
            >
              {button.title}
            </Button>
          )
        })}
        <Button
          key={navigationTranslations.logout}
          className={bem("navigation__button__logout")}
          variant="outlined"
          startIcon={
            <Icon className={bem("navigation__icon")} name={"logout"} />
          }
          onClick={handleLogout}
        >
          {navigationTranslations.logout}
        </Button>
      </>
    )
  }

  const renderMobileNavigation = () => {
    return (
      <FormControl fullWidth sx={{ flex: 1 }}>
        <List component="nav">
          <ListItemButton
            disableRipple
            disableTouchRipple
            aria-controls="lock-menu"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label={messages.pages.account.overview.navigation.label}
            className={bem("navigation__select", anchorEl ? "expanded" : "")}
            id="lock-button"
            sx={{
              "&:hover": {
                backgroundColor: theme.palette.primary.main,
              },
            }}
            onClick={handleClickListItem}
          >
            <ListItemText primary={buttons[selectedNavigationButton].title} />
            <ExpandMore
              sx={{
                scale: 1.25,
                fill: theme.palette.primary.main,
                backgroundColor: theme.palette.secondary.main,
                borderRadius: 25,
              }}
            />
          </ListItemButton>
        </List>
        <Menu
          disableScrollLock
          anchorEl={anchorEl}
          className={bem("navigation__select__menu")}
          elevation={0}
          id="lock-menu"
          open={open}
          MenuListProps={{
            "aria-labelledby": "lock-button",
            role: "listbox",
          }}
          slotProps={{
            paper: {
              sx: {
                width: anchorEl ? anchorEl.offsetWidth : undefined,
                border: "1px solid",
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },
            },
          }}
          onClose={() => setAnchorEl(null)}
        >
          {buttons.map((button, index) => {
            return (
              <MenuItem
                key={button.title}
                disableRipple
                className={bem("navigation__item")}
                selected={index === selectedNavigationButton}
                onClick={() => {
                  setSelectedNavigationButton(index)
                  setAnchorEl(null)
                  router.push(button.url)
                }}
              >
                <Icon className={bem("navigation__icon")} name={button.icon} />
                {button.title}
              </MenuItem>
            )
          })}
          <MenuItem className={bem("navigation__item")} onClick={handleLogout}>
            <Icon className={bem("navigation__icon")} name="logout" />
            {navigationTranslations.logout}
          </MenuItem>
        </Menu>
      </FormControl>
    )
  }

  return (
    <Stack className={bem("navigation")} direction={"column"}>
      {isMobile ? renderMobileNavigation() : renderNavigtion()}
    </Stack>
  )
}
